/**
 * AgentFlow Config API Service
 *
 * RTK Query service for managing the global AgentFlow configuration
 * (system prompt override) and its version history.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CrashingOutGlobalConfig {
  system_prompt: string | null;
}

export interface AgentflowGlobalConfig {
  id: string;
  crashing_out: CrashingOutGlobalConfig;
  default_system_prompt: string;
  selected_version_id: string | null;
  modified_at: string;
  modified_by_id: string | null;
}

export interface UpdateAgentflowGlobalConfig {
  crashing_out?: {
    system_prompt: string | null;
  };
}

export interface AgentflowConfigVersion {
  id: string;
  version_number: number;
  system_prompt: string | null;
  created_by_id: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// API Service
// ---------------------------------------------------------------------------

export const agentflowConfigApiClient = createApi({
  reducerPath: 'agentflowConfigApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['AgentflowConfig', 'AgentflowConfigVersions'] as const,
  endpoints: (builder) => ({
    getAgentflowConfig: builder.query<AgentflowGlobalConfig, void>({
      query: () => 'api/agentflow/config/',
      providesTags: ['AgentflowConfig'],
    }),

    updateAgentflowConfig: builder.mutation<AgentflowGlobalConfig, UpdateAgentflowGlobalConfig>({
      query: (body) => ({
        url: 'api/agentflow/config/',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AgentflowConfig', 'AgentflowConfigVersions'],
    }),

    getAgentflowConfigVersions: builder.query<AgentflowConfigVersion[], void>({
      query: () => 'api/agentflow/config/versions/',
      providesTags: ['AgentflowConfigVersions'],
    }),
  }),
});

export const {
  useGetAgentflowConfigQuery,
  useUpdateAgentflowConfigMutation,
  useGetAgentflowConfigVersionsQuery,
} = agentflowConfigApiClient;
